// ── The reload of an unattended page after a crash, with a back-off ──────────
// #604 review (logic-reviewer §10). Nobody operates the broadcast overlay, so
// the overlay loads the page again after a crash. A crash from a data state
// repeats at each load. A fixed wait of 10 s caused about 6 reloads a minute,
// and each reload sent an exception and the first reads of the page. The wait
// grows to 5 minutes, and it does not grow more: the page still recovers from
// a short fault. The count lives in the session storage. It survives the
// reload of this tab, and a new tab starts again at 0.
// ─────────────────────────────────────────────────────────────────────────────

import { guard, session, type SafeStorage } from './safeStorage';

const KEY = 'lmf:crash-reloads';

/** The wait before reload 1, 2, 3 and 4. The last wait repeats. */
export const RELOAD_WAITS_MS = [10_000, 30_000, 120_000, 300_000] as const;

/** The wait before the next reload, after `count` consecutive reloads. */
export function reloadWait(count: number): number {
  return RELOAD_WAITS_MS[Math.min(Math.max(0, count), RELOAD_WAITS_MS.length - 1)];
}

/** The storage of the tab, or the one that the caller passes (a test passes a fake, a blocked one, or `null`). */
const tab = (storage?: Storage | null): SafeStorage => (storage === undefined ? session : guard(storage));

/** Count one more reload. Return the wait before it. With a blocked
 *  storage, return the first wait, and the reload still runs. */
export function nextCrashReload(storage?: Storage | null): number {
  const store = tab(storage);
  const count = Number(store.get(KEY)) || 0;
  store.set(KEY, String(count + 1));
  return reloadWait(count);
}

/** The page ran with no crash. After the next crash, the reload uses the first wait again. */
export function clearCrashReloads(storage?: Storage | null): void {
  tab(storage).remove(KEY);
}
